import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function CTA() {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const message = formData.get('talep_detayi') as string;
    if (message && message.length > 500) {
      alert('Talep detayı 500 karakterden fazla olamaz.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4e6uaasfh5n2o5bf65g?email=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          setIsFormOpen(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-10 text-teal-50">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => window.location.href = 'tel:+905346818688'}
                className="bg-white text-teal-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg font-semibold whitespace-nowrap cursor-pointer"
              >
                {t('cta.button')}
              </button>
              <button 
                onClick={() => setIsFormOpen(true)}
                className="bg-teal-800 text-white px-8 py-4 rounded-lg hover:bg-teal-900 transition-colors text-lg font-semibold whitespace-nowrap cursor-pointer"
              >
                Online Teklif Formu
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Teklif Formu</h3>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="p-6">
              {/* Kişi/Kurum Bilgileri */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Kişi/Kurum Bilgileri</h4>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="ad_soyad" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="ad_soyad"
                      name="ad_soyad"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder={t('contact.name')}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contact.phone')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="0534 681 8688"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contact.email')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder={t('contact.email')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="adres" className="block text-sm font-medium text-gray-700 mb-1">
                      Adres
                    </label>
                    <input
                      type="text"
                      id="adres"
                      name="adres"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="İstanbul, Türkiye"
                    />
                  </div>
                </div>
              </div>

              {/* Talep Bilgileri */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Talep Bilgileri</h4>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="hizmet_turu" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.service')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="hizmet_turu"
                      name="hizmet_turu"
                      required
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">{t('contact.service.select')}</option>
                      <option value="Ofis Temizliği">{t('contact.service.office')}</option>
                      <option value="Otel Temizliği">Otel Temizliği</option>
                      <option value="Hastane Temizliği">{t('contact.service.hospital')}</option>
                      <option value="AVM Temizliği">{t('contact.service.mall')}</option>
                      <option value="Fabrika Temizliği">{t('contact.service.industrial')}</option>
                      <option value="Okul Temizliği">Okul Temizliği</option>
                      <option value="Dış Cephe Temizliği">Dış Cephe Temizliği</option>
                      <option value="Halı Yıkama">Halı Yıkama</option>
                      <option value="Koltuk Yıkama">Koltuk Yıkama</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="alan_buyuklugu" className="block text-sm font-medium text-gray-700 mb-1">
                        Alan Büyüklüğü (m²)
                      </label>
                      <input
                        type="text"
                        id="alan_buyuklugu"
                        name="alan_buyuklugu"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Örn: 500 m²"
                      />
                    </div>

                    <div>
                      <label htmlFor="temizlik_sikligi" className="block text-sm font-medium text-gray-700 mb-1">
                        Temizlik Sıklığı
                      </label>
                      <select
                        id="temizlik_sikligi"
                        name="temizlik_sikligi"
                        className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        <option value="Günlük">Günlük</option>
                        <option value="Haftalık">Haftalık</option>
                        <option value="Aylık">Aylık</option>
                        <option value="Tek Seferlik">Tek Seferlik</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="talep_detayi" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.message')}
                    </label>
                    <textarea
                      id="talep_detayi"
                      name="talep_detayi"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder={t('contact.message')}
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maksimum 500 karakter</p>
                  </div>
                </div>
              </div>

              {/* Submit Status */}
              {submitStatus === 'success' && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  <i className="ri-check-line mr-2"></i>
                  {t('contact.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('contact.error')}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('contact.sending') : t('contact.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}